import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import DesktopAppCard from "../components/DesktopAppCard";
import { toast } from "sonner";
import {
    IdentificationCard,
    Trash,
    PencilSimple,
    Clock,
    CheckCircle,
    XCircle,
} from "@phosphor-icons/react";

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [payments, setPayments] = useState([]);

    const load = async () => {
        try {
            const [t, p] = await Promise.all([
                api.get("/templates"),
                api.get("/payments/my"),
            ]);
            setTemplates(t.data);
            setPayments(p.data);
        } catch {}
    };

    useEffect(() => {
        load();
        refresh();
    }, []);

    const deleteTpl = async (tid) => {
        if (!confirm("Delete this project?")) return;
        await api.delete(`/templates/${tid}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10"
            data-testid="user-dashboard"
        >
            {/* Welcome banner */}
            <div className="relative overflow-hidden mb-8 p-8 g-violet text-white shadow-glow-violet">
                <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full g-pink opacity-40 blur-3xl" />
                <div className="relative flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-white/70">
                            Dashboard
                        </div>
                        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
                            Welcome back, {user?.name?.split(" ")[0]} 👋
                        </h1>
                        <p className="text-white/80 text-sm mt-2 max-w-md">
                            You've got {user?.credits_remaining ?? 0} card
                            credits and {templates.length} project
                            {templates.length === 1 ? "" : "s"} in your studio.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Link
                            to="/pricing"
                            className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-5 py-2.5 font-medium text-sm"
                        >
                            Buy credits
                        </Link>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
                <div className="relative overflow-hidden card-surface p-5">
                    <div className="absolute top-0 left-0 right-0 h-1 g-violet" />
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Credits
                    </div>
                    <div className="font-display font-black text-4xl tracking-tighter mt-1 bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-blue-600">
                        {user?.credits_remaining ?? 0}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        cards remaining
                    </div>
                </div>
                <div className="relative overflow-hidden card-surface p-5">
                    <div className="absolute top-0 left-0 right-0 h-1 g-ocean" />
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Projects
                    </div>
                    <div className="font-display font-black text-4xl tracking-tighter mt-1 bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">
                        {templates.length}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        templates saved
                    </div>
                </div>
                <div className="relative overflow-hidden card-surface p-5">
                    <div
                        className={`absolute top-0 left-0 right-0 h-1 ${user?.is_verified ? "g-emerald" : "g-amber"}`}
                    />
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Status
                    </div>
                    <div className="font-display font-black text-2xl tracking-tighter mt-1">
                        {user?.is_verified ? (
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-600">
                                ✓ Verified
                            </span>
                        ) : (
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-500 to-orange-500">
                                ⏳ Pending
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        {user?.is_verified
                            ? "Full access unlocked"
                            : "Awaiting admin approval"}
                    </div>
                </div>
            </div>

            <DesktopAppCard />

            {/* Templates */}
            <h2 className="font-display font-black text-2xl tracking-tighter mb-4">
                Your Projects
            </h2>
            {templates.length === 0 ? (
                <div className="card-surface p-10 text-center">
                    <IdentificationCard
                        size={42}
                        weight="duotone"
                        className="mx-auto mb-3 text-zinc-400"
                    />
                    <div className="font-display font-bold text-lg mb-1">
                        No projects yet
                    </div>
                    <div className="text-sm text-zinc-500">
                        New projects are created in the IDCardGen Desktop
                        Studio — download it above.
                    </div>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
                    {templates.map((t) => (
                        <div
                            key={t.id}
                            className="card-surface p-5 hover:-translate-y-1 hover:shadow-md"
                            data-testid={`tpl-card-${t.id}`}
                        >
                            <div className="aspect-[86/54] bg-zinc-100 border border-zinc-200 checker-pattern mb-3 flex items-center justify-center">
                                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                                    {t.width_mm}×{t.height_mm}mm
                                </div>
                            </div>
                            <div className="font-display font-bold truncate">
                                {t.name}
                            </div>
                            <div className="text-xs text-zinc-500 capitalize">
                                {t.layout} ·{" "}
                                {t.card_type === "both"
                                    ? "Front & Back"
                                    : "Front Only"}
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Link
                                    to={`/studio/${t.id}`}
                                    className="btn-primary flex-1 flex items-center justify-center gap-1 text-xs"
                                    data-testid={`tpl-open-${t.id}`}
                                >
                                    <PencilSimple size={13} /> Open
                                </Link>
                                <button
                                    className="btn-secondary flex items-center gap-1 text-xs"
                                    onClick={() => deleteTpl(t.id)}
                                    data-testid={`tpl-delete-${t.id}`}
                                >
                                    <Trash size={13} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Payments */}
            <h2 className="font-display font-black text-2xl tracking-tighter mb-4">
                Your Payments
            </h2>
            <div className="card-surface overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                        <tr className="text-xs uppercase tracking-widest text-zinc-500">
                            <th className="text-left p-3">Package</th>
                            <th className="text-left p-3">Amount</th>
                            <th className="text-left p-3">Ref</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.length === 0 && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="p-6 text-center text-zinc-500"
                                >
                                    No payments yet
                                </td>
                            </tr>
                        )}
                        {payments.map((p) => (
                            <tr
                                key={p.id}
                                className="border-b border-zinc-100"
                                data-testid={`payment-row-${p.id}`}
                            >
                                <td className="p-3 font-medium">
                                    {p.package_name}
                                </td>
                                <td className="p-3 font-mono">
                                    ₹{Number(p.amount ?? 0).toFixed(0)}
                                </td>
                                <td className="p-3 font-mono text-xs text-zinc-500">
                                    {p.transaction_ref || "—"}
                                </td>
                                <td className="p-3 text-xs text-zinc-500">
                                    {new Date(p.created_at).toLocaleString()}
                                </td>
                                <td className="p-3">
                                    {p.status === "pending" && (
                                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                                            <Clock size={13} /> Pending
                                        </span>
                                    )}
                                    {p.status === "approved" && (
                                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                                            <CheckCircle
                                                size={13}
                                                weight="fill"
                                            />{" "}
                                            Approved
                                        </span>
                                    )}
                                    {p.status === "rejected" && (
                                        <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                                            <XCircle size={13} weight="fill" />{" "}
                                            Rejected
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    );
}
